import { useState } from 'react';
import type React from 'react';
import { observer } from 'mobx-react';
import * as DT from '@/engine/PlanningAlgorithm/DataTypes.ts';
import TableRow from '@mui/material/TableRow';
import { getConditionalMeasurementColor, getConditionalStyles } from '@/components/boxes/styles.ts';
import { CriteriaTableCell } from '@/components/boxes/common/Criteria/CriteriaTableCell/CriteriaTableCell.tsx';
import TableCell from '@mui/material/TableCell';
import { StyledTableCell, UpdatableResetIconButton } from '@/components/boxes/BoxTools.tsx';
import { UpdatableValue } from '@/components/boxes/common/UpdatableValue.tsx';
import { EyeCheckboxButton, PenCheckboxButton } from '@/components/boxes/CheckboxButton';
import type { MeasurementProps } from '@/components/boxes/measurementBoxes/types.ts';
import type { BoolFunction } from '@/components/boxes/hooks/types.ts';
import { useTheme } from '@mui/material/styles';
import type { MeasurementBoxesController } from '@/components/boxes/measurementBoxes';
import { CriteriaTooltip } from '@/components/boxes/common/Criteria/CriteriaTooltip.tsx';
import { useCriteria } from '../../common/Criteria/useCriteria';

interface Props {
  measurement: MeasurementProps;
  mainController: MeasurementBoxesController;
  isSelected: BoolFunction;
  isHighlighted: BoolFunction;
  hasCriteria: boolean | undefined;
  index: number;
}

export const DistanceMeasurementRow: React.FC<Props> = observer(
  ({ measurement, mainController, isSelected, isHighlighted, hasCriteria, index }) => {
    const unitOfMeasurement = 'mm';
    const theme = useTheme();

    const isItemSelected: boolean = isSelected(measurement.descriptor);
    const isItemHighlighted: boolean = isHighlighted(measurement.descriptor);

    const { criteriaStatus, criteriaMessage } = useCriteria(measurement.descriptor, unitOfMeasurement);

    const [isTooltipOpen, setIsTooltipOpen] = useState(false);

    const handleTooltipDisabled = (isDisabled: boolean): void => {
      setIsTooltipOpen(isDisabled);
    };

    return (
      <CriteriaTooltip title={criteriaMessage} key={measurement.descriptor + index} open={isTooltipOpen}>
        <TableRow
          key={measurement.label}
          data-testid={measurement.descriptor + 'Distance'}
          sx={{
            ...{
              '&:last-child td, &:last-child th': { border: 0 },
              '&:hover button.locate-button': {
                opacity: 1
              },
              '& button.locate-button': { opacity: 0.1 }
            },
            ...getConditionalStyles(
              isItemHighlighted,
              theme,
              { paddingLeft: 0.3, paddingTop: 0, paddingBottom: 0 },
              {
                paddingRight: 0.5,
                paddingTop: 0,
                paddingBottom: 0
              },
              criteriaStatus,
              measurement.isLabelColored ? measurement.color : undefined
            )
          }}
          onMouseEnter={(): void => {
            mainController.setMeasurementHighlighting(measurement.descriptor, true);
            setIsTooltipOpen(true);
          }}
          onMouseLeave={(): void => {
            mainController.setMeasurementHighlighting(measurement.descriptor, false);
            setIsTooltipOpen(false);
          }}
          selected={isItemSelected}
          onClick={(): void => {
            mainController.setMeasurementSelection(measurement.descriptor, !isItemSelected);
          }}
        >
          {hasCriteria && <CriteriaTableCell criteriaStatus={criteriaStatus} index={index} />}

          <TableCell
            component="th"
            scope="row"
            key={'label' + index}
            sx={{
              paddingLeft: hasCriteria ? 0 : '1rem',
              fontWeight: measurement.isLabelColored ? 'bold' : 'normal',
              color: getConditionalMeasurementColor(
                hasCriteria,
                criteriaStatus,
                theme,
                measurement.isLabelColored ? measurement.color : undefined
              )
            }}
          >
            {measurement.label}
          </TableCell>
          <StyledTableCell className="limited-width light-fill" align="right">
            {measurement.beingEdited !== undefined ? (
              <UpdatableResetIconButton
                descriptor={measurement.descriptor}
                controller={mainController}
                handleTooltipDisabled={handleTooltipDisabled}
              />
            ) : (
              <div />
            )}
          </StyledTableCell>
          <TableCell
            size="small"
            align="right"
            key={'value' + index}
            sx={{
              '&': {
                paddingRight: 0,
                width: '4rem',
                color: getConditionalMeasurementColor(hasCriteria, criteriaStatus, theme)
              }
            }}
          >
            <UpdatableValue
              descriptor={measurement.descriptor}
              constructor={DT.DistanceMeasurement}
              valueKey="distance"
            />{' '}
            {unitOfMeasurement}
          </TableCell>
          <StyledTableCell
            onMouseEnter={(): void => {
              setIsTooltipOpen(false);
            }}
            onMouseLeave={(): void => {
              setIsTooltipOpen(true);
            }}
            align="right"
            key={'beingEdited' + index}
            className="limited-width"
            onClick={(event): void => event.stopPropagation()}
          >
            {measurement.beingEdited !== undefined ? (
              <PenCheckboxButton
                onChange={(checked): void => {
                  mainController.setMeasurementBeingEdited(measurement.descriptor, checked);
                }}
                checked={measurement.beingEdited}
                key={'beingEditedBtn' + index}
              />
            ) : (
              <div />
            )}
          </StyledTableCell>
          <StyledTableCell
            onMouseEnter={(): void => {
              setIsTooltipOpen(false);
            }}
            onMouseLeave={(): void => {
              setIsTooltipOpen(true);
            }}
            align="right"
            key={'visibility' + index}
            className="limited-width"
            onClick={(event): void => event.stopPropagation()}
          >
            <EyeCheckboxButton
              onChange={(checked): void => mainController.setMeasurementVisibility(measurement.descriptor, checked)}
              checked={measurement.visible}
            />
          </StyledTableCell>
        </TableRow>
      </CriteriaTooltip>
    );
  }
);
